/**
 * Presentation for the dashboard's "needs your attention" feed. The API
 * emits a `kind` per item; this map supplies the icon and the call-to-action
 * verb. Unknown kinds fall back to a generic row so a new server kind never
 * breaks the page.
 */
export const ATTENTION_KINDS = {
    expiring_soon: {icon: 'fa-calendar-clock', cta: 'Open Course'},
    study_plan_not_submitted: {icon: 'fa-list-check', cta: 'Set Up Study Plan'},
    study_plan_changes_requested: {icon: 'fa-pen-to-square', cta: 'Revise Study Plan'},
    study_plan_submitted: {icon: 'fa-hourglass-half', cta: 'View Study Plan'},
    milestone_due: {icon: 'fa-comment-pen', cta: 'Write Reflection'},
    milestone_changes_requested: {icon: 'fa-comment-pen', cta: 'Revise Reflection'},
    milestone_submitted: {icon: 'fa-hourglass-half', cta: 'View Reflection'},
    needs_completion_confirm: {icon: 'fa-flag-checkered', cta: 'Finish Course'},
    ready_to_apply: {icon: 'fa-file-certificate', cta: 'Apply'},
    incomplete_required_apps: {icon: 'fa-file-exclamation', cta: 'Complete Form'},
    application_changes_requested: {icon: 'fa-file-pen', cta: 'Revise'},
    application_rejected: {icon: 'fa-file-xmark', cta: 'Review'},
    application_unpaid: {icon: 'fa-credit-card', cta: 'Pay'},
    application_in_progress: {icon: 'fa-file-pen', cta: 'Continue'},
    application_in_review: {icon: 'fa-hourglass-half', cta: 'View'},
    reg_po_pending: {icon: 'fa-file-invoice', cta: 'View'},
    reg_log_unsigned: {icon: 'fa-signature', cta: 'Submit Record'},
    reg_application_changes_requested: {icon: 'fa-file-pen', cta: 'Revise'},
    reg_application_rejected: {icon: 'fa-file-xmark', cta: 'Review'},
    reg_application_unpaid: {icon: 'fa-credit-card', cta: 'Pay'},
    reg_application_in_progress: {icon: 'fa-file-pen', cta: 'Continue'},
    reg_application_in_review: {icon: 'fa-hourglass-half', cta: 'View'},
    tpo_onboarding: {icon: 'fa-file-signature', cta: 'Complete Setup', external: true},
    tpo_agreement_pending: {icon: 'fa-file-signature', cta: 'Sign', external: true},
    sales_tax_unpaid: {icon: 'fa-credit-card', cta: 'Pay', external: true},
    payment_method_missing: {icon: 'fa-credit-card', cta: 'Add Method', external: true},
    cart_quote: {icon: 'fa-shopping-cart', cta: 'Checkout'},
};

export const DEFAULT_ATTENTION = {icon: 'fa-circle-exclamation', cta: 'Open'};

export function attentionMeta(kind) {
    return ATTENTION_KINDS[kind] || DEFAULT_ATTENTION;
}

// Shown wherever a course is waiting on its study plan (mirrors the API's
// STUDY_PLAN_NOTE on attention items)
export const STUDY_PLAN_NOTE = 'Do not start on course work until your study plan is submitted and accepted.';

// The one-line alert for a live enrollment's next step, shared by the
// Course Enrollments card and the dashboard continue tile so the two never
// disagree. Returns null when the next step needs no explanation.
export function nextStepNote(e) {
    switch (e && e.next_step) {
        case 'setup': return STUDY_PLAN_NOTE;
        case 'milestone': return 'A reflection is due before you can continue with the course.';
        case 'confirm_complete': return 'All coursework is done. Confirm to finish the course.';
        case 'apply': return e.isMissingRequiredEndApps
            ? 'Coursework finished. An application or form is required to complete this course.'
            : 'Coursework finished. Applications or forms are available to complete.';
        default: return null;
    }
}

// Labels for the "continue" tile button by the feed's next_step key
export const NEXT_STEP_LABELS = {
    setup: 'Set Up Study Plan',
    milestone: 'Write Reflection',
    confirm_complete: 'Finish Course',
    apply: 'Apply for Certificate',
    lesson: 'Continue',
    log_time: 'Log Study Time',
    review: 'Review Course',
    payment_locked: 'Locked',
};

export const NEXT_STEP_ICONS = {
    setup: 'fa-list-check',
    milestone: 'fa-comment-pen',
    confirm_complete: 'fa-flag-checkered',
    apply: 'fa-file-certificate',
    lesson: 'fa-pencil',
    log_time: 'fa-clock',
    review: 'fa-eye',
    payment_locked: 'fa-lock',
};
